import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { describeApiError } from '@/common/utils/apiError';
import { fetchTramitesKanbanPage } from '@/services/tramitesKanbanService';
import { KANBAN_QUERY_KEY } from './kanbanUtils';
import { KanbanColumnSortMenu } from './KanbanColumnSortMenu';
import { TramiteKanbanCard } from './TramiteKanbanCard';
import { useInfiniteScrollSentinel } from './useInfiniteScrollSentinel';
import type {
  KanbanColumnConfig,
  KanbanFilters,
  KanbanSort,
  ParticipantGroup,
  TramiteKanbanItem,
} from './types';

export { KANBAN_QUERY_KEY };

interface KanbanColumnProps {
  column: KanbanColumnConfig;
  filters: KanbanFilters;
  sort: KanbanSort;
  isDefaultSort: boolean;
  onSortChange: (sort: KanbanSort) => void;
  onSortReset: () => void;
  canViewStatus: boolean;
  canViewTareas: boolean;
  canViewIndicators?: boolean;
  canEditAssignedUser: boolean;
  canBloquear?: boolean;
  canArchivar?: boolean;
  onViewTramite: (tramite: TramiteKanbanItem) => void;
  onViewParticipants: (
    tramite: TramiteKanbanItem,
    group: ParticipantGroup,
  ) => void;
  onViewStatus: (tramite: TramiteKanbanItem) => void;
  onViewTareas: (tramite: TramiteKanbanItem) => void;
  onEditAssignedUser: (tramite: TramiteKanbanItem) => void;
  onClickBloqueo?: (tramite: TramiteKanbanItem) => void;
  onClickArchivado?: (tramite: TramiteKanbanItem) => void;
}

/**
 * Aplana las páginas descartando repetidos por id.
 *
 * La paginación por OFFSET no puede garantizar que una fila aparezca una sola
 * vez: entre dos pedidos alguien puede subir una escritura y correr todo el
 * listado un lugar. Sin esto, esa fila entra dos veces y React recibe dos
 * elementos con la misma `key`.
 */
const flattenUniqueRows = (pages: { data: TramiteKanbanItem[] }[] | undefined) => {
  const seen = new Set<string>();
  const rows: TramiteKanbanItem[] = [];

  (pages ?? []).forEach((page) => {
    page.data.forEach((row) => {
      const key = String(row.id);
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    });
  });

  return rows;
};

export const KanbanColumn = ({
  column,
  filters,
  sort,
  isDefaultSort,
  onSortChange,
  onSortReset,
  canViewStatus,
  canViewTareas,
  canViewIndicators,
  canEditAssignedUser,
  canBloquear,
  canArchivar,
  onViewTramite,
  onViewParticipants,
  onViewStatus,
  onViewTareas,
  onEditAssignedUser,
  onClickBloqueo,
  onClickArchivado,
}: KanbanColumnProps) => {
  const {
    data,
    error,
    isLoading,
    isError,
    isFetchNextPageError,
    isRefetchError,
    isFetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...KANBAN_QUERY_KEY, column.id, filters, sort],
    queryFn: ({ pageParam, signal }) =>
      fetchTramitesKanbanPage({
        status: column.id,
        page: pageParam,
        filters,
        sort,
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined,
    meta: { operation: `kanban:columna:${column.id}` },
  });

  const tramites = useMemo(() => flattenUniqueRows(data?.pages), [data?.pages]);
  const total = data?.pages[data.pages.length - 1]?.total;
  const hasRows = tramites.length > 0;

  // El motivo real importa: un 429 se resuelve esperando, un corte de red
  // reintentando, y un 500 avisando. Un cartel genérico los vuelve
  // indistinguibles y por eso este problema tardó en diagnosticarse.
  const errorInfo = error ? describeApiError(error) : null;

  // Falló traer la tanda siguiente, con tarjetas ya en pantalla.
  const nextPageFailed = isFetchNextPageError && hasRows;
  // Falló refrescar lo ya cargado: las tarjetas siguen siendo válidas, pero
  // pueden estar desactualizadas. No es lo mismo que "no se pudieron cargar más".
  const refreshFailed = isRefetchError && hasRows;
  const initialLoadFailed = isError && !hasRows;

  const isBlockedByError = nextPageFailed || refreshFailed;

  // Al reordenar, la lista ya no es la misma: volver arriba en vez de dejar al
  // usuario mirando una posición intermedia arbitraria
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [sort.sortField, sort.sortDirection]);

  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>({
    enabled: Boolean(hasNextPage) && !isFetchingNextPage && !isError,
    onIntersect: () => {
      void fetchNextPage();
    },
    rootRef: scrollAreaRef,
  });

  return (
    <section
      className={cn(
        'flex min-h-[480px] snap-start flex-col rounded-xl border border-t-4 bg-slate-100 p-2',
        column.accentClassName,
      )}
      aria-labelledby={`kanban-column-${column.id}`}
    >
      <header className="mb-2 flex items-center justify-between gap-2 px-1 py-1">
        <h2
          id={`kanban-column-${column.id}`}
          className="flex min-w-0 items-center gap-2 text-sm font-semibold"
        >
          <span
            className={cn('size-2 shrink-0 rounded-full', column.dotClassName)}
            aria-hidden="true"
          />
          <span className="truncate" title={column.title}>
            {column.title}
          </span>
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          {isLoading ? (
            <Skeleton className="h-5 w-8 rounded-full" />
          ) : (
            <span
              className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-muted-foreground shadow-sm"
              aria-label={`${total ?? 0} escrituras`}
            >
              {total ?? 0}
            </span>
          )}
          <KanbanColumnSortMenu
            status={column.id}
            columnTitle={column.title}
            sort={sort}
            isDefaultSort={isDefaultSort}
            onSortChange={onSortChange}
            onSortReset={onSortReset}
          />
        </div>
      </header>

      <div
        ref={scrollAreaRef}
        className="max-h-[max(380px,calc(100dvh-19rem))] min-h-[380px] space-y-2 overflow-x-hidden overflow-y-auto pr-0.5"
      >
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={`${column.id}-skeleton-${index}`}
              className="h-64 rounded-xl"
            />
          ))
        ) : initialLoadFailed ? (
          <div
            role="alert"
            className="rounded-xl border border-dashed border-red-300 bg-white/60 px-3 py-8 text-center text-xs text-muted-foreground"
          >
            <p className="mb-1 font-medium text-foreground">
              No se pudieron cargar las escrituras.
            </p>
            {errorInfo?.message && <p className="mb-3">{errorInfo.message}</p>}
            <Button
              size="sm"
              variant="outline"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 size-3.5" />
              )}
              Reintentar
            </Button>
          </div>
        ) : !hasRows ? (
          <div className="rounded-xl border border-dashed bg-white/60 px-3 py-8 text-center text-xs text-muted-foreground">
            No hay escrituras en este estado.
          </div>
        ) : (
          <>
            {refreshFailed && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              >
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium">Estás viendo datos sin actualizar.</p>
                  {errorInfo?.message && <p className="mt-0.5">{errorInfo.message}</p>}
                  <button
                    type="button"
                    className="mt-1 font-medium underline underline-offset-2 disabled:opacity-60"
                    disabled={isFetching}
                    onClick={() => void refetch()}
                  >
                    Actualizar ahora
                  </button>
                </div>
              </div>
            )}

            {tramites.map((tramite) => (
              <TramiteKanbanCard
                key={tramite.id}
                tramite={tramite}
                status={column.id}
                canViewStatus={canViewStatus}
                canViewTareas={canViewTareas}
                canViewIndicators={canViewIndicators}
                canEditAssignedUser={canEditAssignedUser}
                canBloquear={canBloquear}
                canArchivar={canArchivar}
                onViewTramite={onViewTramite}
                onViewParticipants={onViewParticipants}
                onViewStatus={onViewStatus}
                onViewTareas={onViewTareas}
                onEditAssignedUser={onEditAssignedUser}
                onClickBloqueo={onClickBloqueo}
                onClickArchivado={onClickArchivado}
              />
            ))}

            {hasNextPage && !isBlockedByError && (
              <div ref={sentinelRef} className="h-4" aria-hidden="true" />
            )}

            {isFetchingNextPage ? (
              <div
                className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground"
                role="status"
              >
                <Loader2 className="size-3.5 animate-spin" />
                Cargando más escrituras...
              </div>
            ) : nextPageFailed ? (
              <div
                role="alert"
                className="rounded-xl border border-dashed border-red-300 bg-white/60 px-3 py-4 text-center text-xs text-muted-foreground"
              >
                <p className="mb-1 font-medium text-foreground">
                  No se pudieron cargar más escrituras.
                </p>
                {errorInfo?.message && <p className="mb-3">{errorInfo.message}</p>}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isFetching}
                  onClick={() => void fetchNextPage()}
                >
                  {isFetching ? (
                    <Loader2 className="mr-1 size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 size-3.5" />
                  )}
                  Reintentar
                </Button>
              </div>
            ) : (
              // Siempre visible, no sólo cuando falta `IntersectionObserver`.
              // El observer puede existir y aun así no disparar nunca si el
              // layout cambia, y sin este botón la columna queda planta sin que
              // el usuario tenga forma de seguir. Con el observer sano casi no
              // se ve: la carga anticipada ocurre antes de llegar al fondo.
              hasNextPage && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => void fetchNextPage()}
                >
                  Cargar más
                </Button>
              )
            )}
          </>
        )}
      </div>
    </section>
  );
};
